<script setup lang="ts">
import { storeToRefs } from "pinia";
import { computed } from "vue";
import { useRedistributionStore } from "../../stores/redistributionStore.ts";
import { formatArrivalWeek, formatUsDate } from "../../utils/dateFormatting.ts";

const { bulkOrderDates } = storeToRefs(useRedistributionStore());

const EMPTY_DATE = "—";

function formatOptionalDate(value: string | null): string {
  return value ? formatUsDate(value) : EMPTY_DATE;
}

const bulkOrders = computed(() =>
  bulkOrderDates.value.map((bulkOrder) => ({
    bulkOrderNumber: bulkOrder.bulkOrderNumber,
    intermediateOrderLabel: `${String(bulkOrder.intermediateOrderCount)} intermediate ${bulkOrder.intermediateOrderCount === 1 ? "order" : "orders"}`,
    arrivalWeek: bulkOrder.projectedArrivalDate
      ? formatArrivalWeek(bulkOrder.projectedArrivalDate)
      : EMPTY_DATE,
    vendorCutDate: formatOptionalDate(bulkOrder.vendorCutDate),
    shipWindow:
      bulkOrder.shipDateRangeStart || bulkOrder.shipDateRangeEnd
        ? `${formatOptionalDate(bulkOrder.shipDateRangeStart)} – ${formatOptionalDate(bulkOrder.shipDateRangeEnd)}`
        : EMPTY_DATE,
    lastDistributionDate: formatOptionalDate(bulkOrder.lastDistributionDate),
  }))
);
</script>

<template>
  <div class="bulk-order-dates" data-testid="bulk-order-dates">
    <p
      v-if="bulkOrders.length === 0"
      class="bulk-order-dates-empty"
      data-testid="bulk-order-dates-empty"
    >
      No Bulk Order date information for this run
    </p>
    <div
      v-for="bulkOrder in bulkOrders"
      :key="bulkOrder.bulkOrderNumber"
      class="bulk-order-dates-entry"
    >
      <div class="flex flex-wrap items-baseline gap-x-3 gap-y-1">
        <span class="bulk-order-dates-heading">
          Bulk Order {{ bulkOrder.bulkOrderNumber }}
        </span>
        <span class="bulk-order-dates-note">
          {{ bulkOrder.intermediateOrderLabel }}
        </span>
        <span class="bulk-order-dates-week">
          Projected Arrival: {{ bulkOrder.arrivalWeek }}
        </span>
      </div>
      <dl class="bulk-order-dates-facts">
        <div>
          <dt>Vendor Cut</dt>
          <dd>{{ bulkOrder.vendorCutDate }}</dd>
        </div>
        <div>
          <dt>Ship Window</dt>
          <dd>{{ bulkOrder.shipWindow }}</dd>
        </div>
        <div>
          <dt>Last Distribution</dt>
          <dd>{{ bulkOrder.lastDistributionDate }}</dd>
        </div>
      </dl>
    </div>
  </div>
</template>
